<template>
  <!-- Pending: a blocking approval prompt the user must answer. -->
  <div v-if="message.status === 'pending'" class="approval-card my-2 pa-3">
    <div class="d-flex align-center ga-2 mb-1">
      <v-icon icon="mdi-shield-alert-outline" color="warning" size="small" />
      <span class="text-body-2 font-weight-medium">{{ t('approval.required') }}</span>
    </div>

    <div class="text-body-2 mb-1">{{ message.summary || message.description || message.name }}</div>
    <div v-if="message.summary && message.description" class="text-caption text-medium-emphasis mb-2">
      {{ message.description }}
    </div>

    <div v-if="detailRows.length" class="approval-details mb-3">
      <div
        v-for="(row, i) in detailRows"
        :key="i"
        class="approval-row text-body-2"
        :class="{ 'text-medium-emphasis': row.muted }"
        :style="{ paddingLeft: `${row.indent * 16}px` }"
      >
        <span v-if="row.label" class="row-label">{{ row.label }}</span>
        <span class="row-value">{{ row.value }}</span>
      </div>
    </div>

    <div class="d-flex ga-2">
      <v-btn
        size="small"
        color="success"
        variant="flat"
        prepend-icon="mdi-check"
        @click="decide(true)"
      >
        {{ t('approval.approve') }}
      </v-btn>
      <v-btn
        size="small"
        color="error"
        variant="outlined"
        prepend-icon="mdi-close"
        @click="decide(false)"
      >
        {{ t('approval.deny') }}
      </v-btn>
    </div>
  </div>

  <!-- Decided: collapse to a compact status chip. -->
  <div v-else class="my-2">
    <v-chip size="small" :color="chipColor" variant="tonal">
      <v-icon :icon="chipIcon" size="x-small" start />
      {{ chipLabel }}
      <span class="text-medium-emphasis ml-1">· {{ message.summary || message.description || message.name }}</span>
    </v-chip>
  </div>
</template>

<script setup>
import { useI18n } from '../../composables/useI18n.js'
const { t } = useI18n()
import { computed } from 'vue'
import { useChat } from '../../composables/useChat.js'
import { buildApprovalRows } from '../../utils/approvalSummary.js'

const props = defineProps({
  message: { type: Object, required: true },
})

const { approveTool } = useChat()

const detailRows = computed(() => buildApprovalRows(props.message.arguments, props.message.fieldTitles))

function decide(approved) {
  approveTool(props.message.toolCallId, approved)
}

const chipColor = computed(() => {
  switch (props.message.status) {
    case 'approved': return 'success'
    case 'denied': return 'error'
    case 'timeout': return 'warning'
    default: return undefined
  }
})

const chipIcon = computed(() => {
  switch (props.message.status) {
    case 'approved': return 'mdi-check'
    case 'denied': return 'mdi-close'
    case 'timeout': return 'mdi-timer-off-outline'
    default: return 'mdi-circle-small'
  }
})

const chipLabel = computed(() => {
  switch (props.message.status) {
    case 'approved': return t('approval.approvedChip')
    case 'denied': return t('approval.deniedChip')
    case 'timeout': return t('approval.timedOutChip')
    default: return props.message.status
  }
})
</script>

<style scoped>
.approval-card {
  max-width: 85%;
  border-radius: 8px;
  border: 1px solid rgba(var(--v-theme-warning), 0.5);
  background: rgba(var(--v-theme-warning), 0.05);
}
.approval-details {
  border-left: 2px solid rgba(var(--v-theme-warning), 0.3);
  padding: 4px 0 4px 10px;
}
.approval-row {
  display: flex;
  gap: 8px;
  line-height: 1.6;
}
.row-label {
  flex-shrink: 0;
  opacity: 0.65;
}
.row-label::after {
  content: ':';
}
.row-value {
  overflow-wrap: anywhere;
}
</style>
